import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { isTruthy } from "@remi/ts-utils";

import { useTheme } from "../hooks";
import { cn } from "../lib/utils";
import { RemiSpinner } from "./RemiSpinner";
import { TouchTargetsContext } from "./touch-targets-context";

const buttonVariants = cva(
  "inline-flex h-fit items-center justify-center gap-1 truncate whitespace-nowrap font-medium ring-offset-background transition-[background-color,border-color,color,scale] ease-out-strong focus:outline-hidden focus-visible:border-border-dark focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-border-accent active:scale-[0.97] disabled:pointer-events-none disabled:cursor-default",
  {
    variants: {
      fullWidth: {
        true: "w-full max-w-full",
        false: "w-fit min-w-fit max-w-[min(100%,16rem)]",
      },
      variant: {
        primary:
          "border-border-primary bg-button-bgPrimary text-button-iconPrimary text-button-textPrimary hover:bg-button-bgPrimaryHover disabled:bg-button-bgPrimaryDisabled disabled:text-button-iconPrimaryDisabled disabled:text-button-textPrimaryDisabled",
        secondary:
          "border-border-primary bg-button-bgSecondary text-icon-secondary text-text-secondary hover:bg-button-bgSecondaryHover disabled:bg-button-bgSecondaryDisabled disabled:text-button-textSecondaryDisabled disabled:text-icon-tertiary disabled:shadow-none",
        tertiary:
          "bg-button-bgTertiary text-text-secondary hover:bg-button-bgTertiaryHover disabled:text-text-tertiary",
        filled:
          "border-border-primary bg-button-bgTertiaryHover text-text-secondary disabled:text-icon-tertiary disabled:text-text-tertiary",
        destructive:
          "border-border-destructive bg-button-bgDestructive text-button-iconDestructive text-button-textDestructive hover:bg-button-bgDestructiveHover disabled:bg-button-bgDestructiveDisabled",
        accent:
          "border-border-accent bg-background-accentLight text-text-accent disabled:bg-button-bgAccentDisabled disabled:text-icon-tertiary disabled:text-text-tertiary",
        successLight:
          "border-border-success bg-background-successLight text-text-success disabled:bg-button-bgAccentDisabled disabled:text-text-tertiary",
        destructiveLight:
          "border-border-destructive bg-background-destructiveLight text-text-destructive disabled:bg-button-bgAccentDisabled disabled:text-text-tertiary",
        none: "",
      },
      size: {
        mobile: "h-7 min-w-7 px-2 py-1.5 text-xs",
        sm: "h-6 min-w-6 px-2 py-1 text-xs",
        md: "h-7 min-w-7 px-2 py-1.5 text-xs",
        lg: "h-10 min-w-10 p-3 text-sm",
        custom: "",
      },
      system: {
        CRM: "",
        PORTAL: "h-12 min-w-12 sm:h-12 sm:min-w-12 sm:p-3 sm:text-sm",
      },
      appearance: {
        standard: "rounded-md",
        pill: "rounded-full",
      },
      border: {
        true: "border",
        false: "",
      },
      borderStyle: {
        standard: "",
        dashed: "border-[1.5px] border-dashed",
      },
      muted: {
        true: "bg-transparent disabled:bg-transparent",
        false: "",
      },
      elevated: {
        true: "shadow-button",
        false: "",
      },
      empty: {
        true: "text-text-tertiary italic",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        muted: true,
        className:
          "text-text-secondary hover:bg-button-bgTertiaryHover disabled:border-border-tertiary disabled:text-text-tertiary data-[state=open]:bg-button-bgTertiaryHover",
      },
      {
        variant: "secondary",
        muted: true,
        className:
          "text-text-secondary hover:bg-button-bgSecondaryHover disabled:border-border-tertiary disabled:text-text-tertiary",
      },
      {
        variant: "filled",
        muted: true,
        className:
          "text-text-secondary hover:bg-button-bgTertiaryHover disabled:border-border-tertiary disabled:text-text-tertiary",
      },
      {
        variant: "destructive",
        muted: true,
        className:
          "text-text-destructive hover:bg-background-destructiveLight disabled:border-background-destructiveLight disabled:text-icon-destructive",
      },
      {
        variant: "accent",
        muted: true,
        className:
          "text-text-accent hover:bg-background-none disabled:border-background-accentLight disabled:text-icon-accent",
      },
      {
        variant: "filled",
        empty: true,
        className: "text-text-tertiary italic",
      },
    ],
    defaultVariants: {
      fullWidth: false,
      variant: "primary",
      size: "md",
      elevated: false,
      border: false,
      appearance: "standard",
      muted: false,
      system: "CRM",
      empty: false,
    },
  },
);

type RemiButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
type RemiButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

export interface RemiButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  muted?: boolean;
  elevated?: boolean;
  empty?: boolean;
  border?: boolean;
  borderStyle?: "standard" | "dashed";
  iconPosition?: "left" | "right";
  appearance?: "standard" | "pill";
  state?: "default" | "active";
  childrenClassName?: string;
  fullWidth?: boolean;
}

const RemiButton = ({
  ref,
  className,
  variant = "primary",
  muted = false,
  elevated,
  empty,
  size = "md",
  appearance = "standard",
  border = false,
  asChild = false,
  loading = false,
  disabled = false,
  icon,
  iconPosition = "left",
  endIcon,
  children,
  type = "button",
  childrenClassName = "w-fit truncate",
  borderStyle = "standard",
  fullWidth = false,
  ...props
}: RemiButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const isDisabled = disabled || loading;
  const Component = asChild ? Slot : "button";
  const { system } = useTheme();
  const hasTouchTargets = React.useContext(TouchTargetsContext);
  elevated = elevated === undefined ? variant === "secondary" : elevated;

  const isIconOnly = isTruthy(icon) && !isTruthy(children) && !isTruthy(endIcon);

  let iconOnlyPaddingClass: string | undefined;
  if (isIconOnly && size != null && size !== "custom") {
    iconOnlyPaddingClass = size === "lg" ? "p-2" : "p-1";
  }

  const iconClass = cn(
    children !== undefined && (size === "sm" ? "min-w-[12px]" : "min-w-[16px]"),
    variant === "filled" && empty === true && "text-icon-tertiary",
    isDisabled && "opacity-80",
  );

  return (
    <Component
      type={type}
      disabled={isDisabled}
      aria-busy={loading}
      className={
        variant === "none"
          ? className
          : cn(
              buttonVariants({
                variant,
                size,
                system,
                appearance,
                border,
                borderStyle,
                muted,
                elevated,
                empty,
                fullWidth,
              }),
              iconOnlyPaddingClass,
              hasTouchTargets && size !== "custom" && "h-11 min-h-11 min-w-11 px-3 text-sm",
              className,
            )
      }
      ref={ref}
      {...props}
    >
      {loading && iconPosition === "left" && (
        <div className="w-4">
          <RemiSpinner size="sm" aria-hidden="true" />
        </div>
      )}
      {isTruthy(icon) && iconPosition === "left" && !loading && <span className={iconClass}>{icon}</span>}
      {isTruthy(children) && <span className={childrenClassName}>{children}</span>}
      {loading && iconPosition === "right" && <RemiSpinner size="sm" aria-hidden="true" />}
      {isTruthy(icon) && iconPosition === "right" && !loading && <span className={iconClass}>{icon}</span>}
      {isTruthy(endIcon) && !loading && endIcon}
    </Component>
  );
};

RemiButton.displayName = "RemiButton";

export { RemiButton, type RemiButtonSize, type RemiButtonVariant };
